import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import api from '../../api';
import { toast } from 'sonner';
import { Input } from './NewsManager';

const SettingsManager = () => {
  const [config, setConfig] = useState(null);

  useEffect(() => { api.get('/site-config').then((r) => setConfig(r.data)); }, []);
  if (!config) return <p className="text-white/50">Chargement...</p>;

  const save = async () => {
    try {
      await api.put('/site-config', {
        clubName: config.clubName,
        tagline: config.tagline,
        contact: config.contact,
        socialLinks: config.socialLinks,
        credits: config.credits || '',
        homeStats: {
          ...(config.homeStats || {}),
          foundedYear: Number(config.homeStats?.foundedYear) || 2012,
        },
      });
      toast.success('Paramètres enregistrés');
    } catch (e) { toast.error('Erreur'); }
  };

  const c = (k, v) => setConfig({ ...config, contact: { ...config.contact, [k]: v } });
  const s = (k, v) => setConfig({ ...config, socialLinks: { ...config.socialLinks, [k]: v } });
  const h = (k, v) => setConfig({ ...config, homeStats: { ...(config.homeStats || {}), [k]: v } });

  return (
    <div data-testid="settings-manager">
      <h2 className="text-2xl font-bold text-white mb-8">Paramètres du site</h2>
      <div className="space-y-6 max-w-3xl">
        <Input label="Nom du club" value={config.clubName} onChange={(v) => setConfig({ ...config, clubName: v })} />
        <Input label="Slogan" value={config.tagline} onChange={(v) => setConfig({ ...config, tagline: v })} />

        <h3 className="text-lg font-semibold text-white pt-4">Coordonnées</h3>
        <div className="grid grid-cols-2 gap-4">
          <Input label="Téléphone" value={config.contact.phone} onChange={(v) => c('phone', v)} />
          <Input label="Email" value={config.contact.email} onChange={(v) => c('email', v)} />
        </div>
        <Input label="Adresse" value={config.contact.address} onChange={(v) => c('address', v)} />

        <h3 className="text-lg font-semibold text-white pt-4">Liens externes</h3>
        <Input label="Facebook" value={config.socialLinks.facebook} onChange={(v) => s('facebook', v)} />
        <Input label="Instagram" value={config.socialLinks.instagram} onChange={(v) => s('instagram', v)} />
        <Input label="YouTube" value={config.socialLinks.youtube} onChange={(v) => s('youtube', v)} />
        <Input label="Mon Club (inscriptions)" value={config.socialLinks.monClub} onChange={(v) => s('monClub', v)} />
        <Input label="Boutique (Hello ASSO)" value={config.socialLinks.boutique} onChange={(v) => s('boutique', v)} />

        <h3 className="text-lg font-semibold text-white pt-4">Statistiques d'accueil</h3>
        <p className="text-white/50 text-xs">Affichées sur la page d'accueil. L'ancienneté est calculée automatiquement depuis "Année de création".</p>
        <div className="grid grid-cols-3 gap-4">
          <Input label="Nombre de gymnastes" value={config.homeStats?.gymnasts || ''} onChange={(v) => h('gymnasts', v)} placeholder="200+" />
          <Input label="Année de création" value={String(config.homeStats?.foundedYear || 2012)} onChange={(v) => h('foundedYear', v)} placeholder="2012" />
          <Input label="Nombre de médailles" value={config.homeStats?.medals || ''} onChange={(v) => h('medals', v)} placeholder="50+" />
        </div>

        <h3 className="text-lg font-semibold text-white pt-4">Crédits photos (pied de page)</h3>
        <Input label="Crédits / droits des photos" value={config.credits || ''} onChange={(v) => setConfig({ ...config, credits: v })} textarea testId="credits-input" />

        <button onClick={save} data-testid="save-settings-btn" className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-fuchsia-500 to-pink-500 rounded-lg text-white font-medium"><Save size={18} /> Enregistrer</button>

        <PasswordChangeSection />
      </div>
    </div>
  );
};

const PasswordChangeSection = () => {
  const [oldPwd, setOld] = useState('');
  const [newPwd, setNew] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (newPwd !== confirm) return toast.error('Les mots de passe ne correspondent pas');
    if (newPwd.length < 8) return toast.error('Minimum 8 caractères');
    setLoading(true);
    try {
      await api.post('/auth/change-password', { old_password: oldPwd, new_password: newPwd });
      toast.success('Mot de passe mis à jour');
      setOld(''); setNew(''); setConfirm('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  };

  return (
    <div className="pt-8 border-t border-white/10 mt-8">
      <h3 className="text-lg font-semibold text-white mb-4">Changer le mot de passe</h3>
      <div className="grid gap-4 max-w-md">
        <Input label="Mot de passe actuel" value={oldPwd} onChange={setOld} type="password" testId="old-password-input" />
        <Input label="Nouveau mot de passe (min. 8 caractères)" value={newPwd} onChange={setNew} type="password" testId="new-password-input" />
        <Input label="Confirmer le nouveau mot de passe" value={confirm} onChange={setConfirm} type="password" testId="confirm-password-input" />
        <button onClick={submit} disabled={loading} data-testid="change-password-btn" className="flex items-center gap-2 px-6 py-3 bg-white/10 border border-fuchsia-500/40 rounded-lg text-white font-medium hover:bg-fuchsia-500/20 disabled:opacity-50 w-fit">
          <Save size={18} /> {loading ? 'Enregistrement...' : 'Mettre à jour le mot de passe'}
        </button>
      </div>
    </div>
  );
};

export default SettingsManager;
